import React, { useState, useEffect, useMemo, use } from "react";
import {
  Space,
  Collapse,
  Form,
  Checkbox,
  Select,
  Col,
  Row,
  Input,
  DatePicker,
  notification,
  Button,
  Upload,
  Tabs,
  Tooltip,
  Popover,
  Drawer,
  Spin,
} from "antd";
import Icon from "@mdi/react";
import {
  mdiAccountMinusOutline,
  mdiMenuDown,
  mdiFileUploadOutline,
  mdiPlusCircle,
  mdiAccount,
  mdiReceiptTextOutline,
  mdiCardAccountDetails,
  mdiInformationOutline,
  mdiEarth,
} from "@mdi/js";
import ListApi, { SelectOption } from "../../../api-services/list-api.service";
import { cabinMetaFromGroups } from "../../../utils/cruise";
import Cookies from "js-cookie";
import { currencyDisplay, scrollIntoView, storedArray$, withMalaysiaFirst } from "../../../utils/helpers";
import {
  b2c2bDateFormat,
  b2c2bUploadProps,
  departureDateFormat,
  passportExpiryDisabledDates,
  phoneNumberRule,
} from "../../../utils/antd.props";
import AgeValidatorField from "./AgeValidatorField";
import PhoneNumberInput from "../../../components/shared/PhoneNumberInput";
import useBreakpoint from "../../../utils/useBreakpoint";
import dayjs from "dayjs";
const customCookies = Cookies.withAttributes({
  expires: 14,
});

const getFile = (e) => e.file;

const PassengerInfo = ({
  priceCurrency,
  pricingTiers,
  updatePrice,
  showRegisterAsMember,
  departureDate,
  pricingCategories,
  prefillDOB,
  enableAddInfant,
  form,
  v2,
  activeTab,
  setActiveTab,
  countries,
  cities,
  states,
  selectedPax,
  onSelectTraveler,
  agentLookupLoading,
  cabinGroups,
  childAgeRanges,
}) => {
  const listApi = new ListApi();

  const cabinMeta = useMemo(() => cabinMetaFromGroups(cabinGroups), [cabinGroups]);

  const [titles, setTitles] = useState<SelectOption[]>([]);
  const [genders, setGenders] = useState<SelectOption[]>([]);
  const [filteredStates, setFilteredStates] = useState<SelectOption[]>([]);
  const [filteredCities, setFilteredCities] = useState<SelectOption[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<SelectOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [isMalaysian, setIsMalaysian] = useState<boolean>(true);
  const [passengerAges, setPassengerAges] = useState<{ [key: number]: number | null }>({});
  const [isMoreDetailsOpen, setIsMoreDetailsOpen] = useState<{ [index: number]: boolean }>({});
  const [passengerRoomCategory, setPassengerRoomCategory] = useState<string[]>([]);
  const isMobile = useBreakpoint();
  const [visaDrawerOpen, setVisaDrawerOpen] = useState(false);

  const nationalityCountries = useMemo(() => withMalaysiaFirst(countries), [countries]);

  const visaPopoverContent = (
    <div className="visa-popover-content">
      <p>VISA may be required. Kindly reconfirm the VISA requirements with your travel agent.</p>
      <p>If a VISA is required, please apply before making the booking. All payments are non-refundable in the event that the visa is not approved.</p>
    </div>
  );

  const visaPopoverTitle = (
    <Space>
      <Icon path={mdiInformationOutline} size="18px" />
      <span>VISA</span>
    </Space>
  );

  const visaBadgeTrigger = (
    <span className="visa-badge">
      <span>VISA</span>
      <Icon path={mdiInformationOutline} size="14px" />
    </span>
  );

  useEffect(() => {
    Promise.all([
      listApi.getTitles(),
      listApi.getGenders(),
    ])
      .then(([titles, genders]) => {
        setFilteredStates(states);
        setFilteredCities(cities);
        setFilteredCountries(countries);
        setTitles(titles);
        setGenders(genders);
      })
      .catch((error) => {
        console.error(error);
        notification.error({ message: "Something went wrong." });
      });
  }, [cities, countries]);

  const getPricingTierForPassenger = (passengerNumber) => {
    return pricingTiers?.filter(
      (tier) => passengerNumber >= tier.start && passengerNumber <= tier.end
    );
  };

  const getPassengerPrice = (passengerNumber, category) => {
    const tier = getPricingTierForPassenger(passengerNumber + 1);
    if (!tier) return 0;

    const price = tier.find(({ name }) => name === category)?.price;
    return currencyDisplay(priceCurrency, price);
  };

  const tabItems = [
    ...form.getFieldValue("passengers")?.map((_, index) => ({
      key: index.toString(),
      label: `Passenger ${index + 1}`,
    })) || [],
    { key: "booking-address", label: "Address" },
  ];

  const onTabChange = async (nextKey) => {
    const currentIndex = tabItems.findIndex(t => t.key === activeTab);
    const nextIndex = tabItems.findIndex(t => t.key === nextKey);

    if (nextIndex < currentIndex) {
      setActiveTab(nextKey);
      return;
    }
  
    handleNext(nextKey);
  };

  // Handle tab change on click of Next button
  const handleNext = async (key) => {
    scrollIntoView("passenger-info");
    
    try {
      const allFields = form.getFieldsError();
      const fieldsToValidate =  allFields
                                  .map(f => f.name)
                                  .filter(name => name[0] === "passengers" && name[1] === Number(activeTab));

      await form.validateFields(fieldsToValidate);

      setActiveTab(key);

    } catch (errorInfo) {
      console.error("Validation Failed:", errorInfo); // Handle validation errors  
    }
  }

  const handleRoomCategoryChange = (fieldName, category) => {
    const roomCategory = category.includes("child") ? "child" : category;
    setPassengerRoomCategory((prevCategories) => ({
      ...prevCategories,
      [fieldName]: roomCategory,
    }));

    // Clear the infants field if the room category is "child"
    if (roomCategory === "child") {
      form.setFieldsValue({
        passengers: {
          [fieldName]: {
            infants: [],
          },
        },
      });
    }

    updatePrice();
  };

  const PassengerForm = (field) => {
    const firstPax = field.name === 0;
    const passengers = form.getFieldsValue()?.passengers || [];
    const infantsCount = passengers.length > 0 ? passengers[field.name].infants.length : 0;
    const isChildCategory = passengers.length > 0 && passengers[field.name]?.category?.includes("child");

    const prefillDOBHandler = (event) => {
      if (isMalaysian) {
        const val = event.target.value.slice(0, 6);
        const dobFromNRIC = dayjs(val, "YYMMDD");
        if(dobFromNRIC.isValid() && isMalaysian) prefillDOB(field.name, dobFromNRIC);
      }
    };

    const calculateAge = (dob) => {
      if (!dob) return null;
      return dayjs(departureDate, departureDateFormat).diff(dayjs(dob), "year");
    };
  
    // Handle DOB change
    const handleDateChange = (date) => {
      const age = calculateAge(date);
      setPassengerAges((prevAges) => ({
        ...prevAges,
        [field.name]: age,
      }));
    };

    const roomsByAge = (category) => {
      const age:number|null = passengerAges[field.name];
      const adultOrSingle = ['adult', 'single_supplement'].includes(category);
      if (adultOrSingle) return false;

      const range = childAgeRanges?.[category];
      if (age == null) return true;
      if (!range) return false;

      return age < range.min_age || age > range.max_age;
    };

    let roomCategories:SelectOption[] = [];
    if (pricingTiers.length > 0) {
      roomCategories = Object.keys(pricingCategories).map((category) => ({
        disabled: roomsByAge(category),
        value: category,
        label: `${pricingCategories[category]} (${getPassengerPrice(
          field.name,
          category
        )})`,
      }));
    }

    const openMoreDetails = () => {
      setIsMoreDetailsOpen((prevState) => ({
        ...prevState,
        [field.name]: !prevState[field.name],
      }));
    }

    const setContactDetailsSameAsFirstPax = (e) => {
      if (e.target.checked) {
        const firstPax = form.getFieldValue(['passengers', 0]);
        form.setFieldValue(['passengers', field.name, 'mobile'], firstPax?.mobile);
        form.setFieldValue(['passengers', field.name, 'email'], firstPax?.email);
      } else {
        form.setFieldValue(['passengers', field.name, 'mobile'], "");
        form.setFieldValue(['passengers', field.name, 'email'], "");
      }
    }

    // Keeps any "same as Passenger 1" passenger's contact fields in sync when Passenger 1's own details change
    const syncLinkedPaxContact = () => {
      if (!firstPax) return;
      const allPassengers = form.getFieldValue('passengers') || [];
      allPassengers.forEach((p, idx) => {
        if (idx > 0 && p?.same_as_first_pax) {
          form.setFieldValue(['passengers', idx, 'mobile'], allPassengers[0]?.mobile);
          form.setFieldValue(['passengers', idx, 'email'], allPassengers[0]?.email);
        }
      });
    }

    return (
      <div className="b2c2b-section_panel-form">
        <Space size={20} direction="vertical" style={{ display: "flex" }}>
          {firstPax && showRegisterAsMember && (
            <Form.Item label="" name={[field.name, "member"]} valuePropName="checked">
              <Checkbox disabled>Register as member</Checkbox>
            </Form.Item>
          )}

          <Row gutter={[20, 20]}>
            <Col xs={24} lg={12}>
              <Form.Item
                label="Designation"
                name={[field.name, "designation"]}
              >
                <Select
                  suffixIcon={<Icon path={mdiMenuDown} size={1} />}
                  placeholder="Select"
                  options={titles}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                label="Fare Category"
                name={[field.name, "category"]}
                rules={[{ required: true, message: `This field cannot be empty${v2 ? ". Please add Travelers" : ""}` }]}
              >
                <Select
                  disabled={v2}
                  suffixIcon={<Icon path={mdiMenuDown} size={1} />}
                  placeholder="Select"
                  options={roomCategories}
                  onChange={(event) => handleRoomCategoryChange(field.name, event)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                label={
                  <>
                    <Space>
                      <span>Family name/Surname on Passport</span>
                      <Tooltip title={
                        <>
                          <div>Passenger Name Guide</div>
                          <div>Surname: Smith</div>
                          <div>Given Name: John</div>
                        </>
                      }>
                        <Icon path={mdiInformationOutline} size="18px" />
                      </Tooltip>
                    </Space>
                  </>
                }
                name={[field.name, "surname"]}
                rules={[{ required: true }]}
              >
                <Input placeholder="Surname on Passport" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                label="Given name on Passport"
                name={[field.name, "given_name"]}
                rules={[{ required: true }]}
              >
                <Input placeholder="Given name on Passport" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                label={
                  v2 ? (
                    <div className="nationality-label">
                      <span>Nationality</span>
                      {isMobile ? (
                        <span className="visa-badge" onClick={() => setVisaDrawerOpen(true)}>
                          <span>VISA</span>
                          <Icon path={mdiInformationOutline} size="14px" />
                        </span>
                      ) : (
                        <Popover content={visaPopoverContent} title={visaPopoverTitle} trigger="hover">
                          {visaBadgeTrigger}
                        </Popover>
                      )}
                    </div>
                  ) : "Nationality"
                }
                name={[field.name, 'nationality']}
                rules={[{ required: true }]}
              >
                <Select showSearch options={nationalityCountries} placeholder="Nationality" onChange={(selected) => setIsMalaysian(selected == "Malaysia")} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <AgeValidatorField
                name={[field.name, 'date_of_birth']}
                rules={[{required: true}]}
                onChange={handleDateChange}
                category={passengers[field.name]?.category}
                departuredate={departureDate}
                childAgeRanges={childAgeRanges}
              />
            </Col>
          </Row>
          
          {
            !firstPax && (
              <Row gutter={[20, 20]}>
                <Col span={24}>
                  <Form.Item 
                    label="Contact Details" 
                    name={[field.name, 'same_as_first_pax']} 
                    layout="horizontal"
                    labelCol={{ span: 4 }} 
                    valuePropName="checked" 
                    className="contact-details-same-as-first-pax"
                    initialValue={false}>
                    <Checkbox defaultChecked onChange={setContactDetailsSameAsFirstPax}>Same as Passenger 1</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            )
          }
          
          <Form.Item noStyle dependencies={[['passengers', field.name, 'same_as_first_pax']]}>
            {() => {
              const sameAsFirst = form.getFieldValue(['passengers', field.name, 'same_as_first_pax']);
              if (firstPax || !sameAsFirst) {
                return (
                  <Row gutter={[20, 20]}>                          
                    <Col xs={24} lg={12}>
                      <Form.Item label="Mobile Number" name={[field.name, 'mobile']} rules={[{required: !sameAsFirst}, phoneNumberRule]}>
                        <PhoneNumberInput onChange={firstPax ? syncLinkedPaxContact : undefined} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Email"
                        name={[field.name, 'email']}
                        rules={[{ type: 'email' }, {required: !sameAsFirst}]}
                        extra={firstPax && agentLookupLoading ? "Checking agent..." : undefined}
                      >
                        <Input placeholder="Email" type="email" onChange={firstPax ? syncLinkedPaxContact : undefined} suffix={firstPax && agentLookupLoading ? <Spin size="small" /> : undefined}/>
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }
              return null;
            }}
          </Form.Item>

          <Row>
            <Col span={24}>            
              <Collapse
                activeKey={isMoreDetailsOpen[field.name] ? "more-details" : undefined} // Dynamically control collapsible
                onChange={(keys) => openMoreDetails} // Update state on change
                className="more-details-collapsible" ghost items={[
                { 
                  key: 'more-details',
                  label: "More Details",
                  children: (
                    <Row gutter={[20, 20]}>
                      <Col xs={24} lg={12}>
                        <Form.Item 
                          label="NRIC" 
                          name={[field.name, 'nric']} 
                          rules={[{ pattern: /^\d{12}$/ }]}>
                          <Input placeholder="900101010001" maxLength={12} onBlur={prefillDOBHandler}/>
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item
                          label="Passport Number"
                          name={[field.name, "passport_number"]}
                        >
                          <Input placeholder="Passport Number" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item
                          label="Passport Expiry"
                          name={[field.name, "passport_expiry_date"]}
                        >
                          <DatePicker
                            format={b2c2bDateFormat}
                            disabledDate={(current) =>
                              passportExpiryDisabledDates(current, departureDate)
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item
                          label="Passport Photocopy"
                          name={[field.name, "as_passport_photocopy"]}
                          getValueFromEvent={getFile}
                          valuePropName="file"
                        >
                          <Upload {...b2c2bUploadProps} maxCount={1} multiple={false}>
                            <Input
                              placeholder="Document name"
                              suffix={<Icon path={mdiFileUploadOutline} size="19px" />}
                            />
                          </Upload>
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          label="Remark"
                          name={[field.name, "customer_remark"]}
                        >
                          <Input.TextArea placeholder="Remark VGML - vegetarian" />
                        </Form.Item>
                      </Col>
                    </Row>
                  ) 
                }
              ]}/>
            </Col>
          </Row>

          {
            passengerRoomCategory[field.name] != 'child' && (
              <Form.Item>
                <Form.List name={[field.name, 'infants']}>
                  {(subFields, subOpt) => (
                    <div className="infants-list">
                      {subFields.map((subField, index) => (
                        <div key={index} className="infants-form">
                          <div className="infants-form_header">
                            <label>Infant {index+1} </label>
                            <Button
                              color="primary"
                              size="small"
                              variant="text"
                              icon={<Icon path={mdiAccountMinusOutline} size={1} />}
                              onClick={() => {
                                subOpt.remove(subField.name);
                                updatePrice()
                              }}
                            />
                          </div>
                          <Row key={subField.key} gutter={[20, 20]}>
                            <Col xs={24} lg={12}>
                              <Form.Item label="Surname on Passport" name={[subField.name, 'surname']}>
                                <Input placeholder="Infants surname" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} lg={12}>
                              <Form.Item label="Given name on Passport" name={[subField.name, 'given_name']}>
                                <Input placeholder="Infants given name" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} lg={12}>
                              <AgeValidatorField agegroup="infant" name={[subField.name, 'date_of_birth']} departuredate={departureDate} rules={[{required: true}]}/>
                            </Col>
                            <Col xs={24} lg={12}>
                              <Form.Item label="Gender" name={[subField.name, 'gender']}>
                                <Select suffixIcon={<Icon path={mdiMenuDown} size={1} />} placeholder="Select" options={genders}/>
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      ))}
                      { (enableAddInfant && infantsCount < 2 && !isChildCategory)&& (
                        <Button
                          className="add-infant-btn" 
                          color="primary" 
                          variant="outlined" 
                          size="small"
                          onClick={() => {subOpt.add(); updatePrice()}}>
                          <Icon path={mdiPlusCircle} size="20px"/> 
                          Add Infant
                        </Button>
                      )}
                    </div>
                  )}
                </Form.List>
              </Form.Item>
            )
          }


          <div className="form-next-btn-wrapper">
            <Button
              className="next-tab-btn-absolute" 
              color="primary" 
              variant="filled"
              onClick={() => {
                const passengersCount = form.getFieldsValue().passengers.length;
                const nextPassengerTab = parseInt(activeTab) + 1;
                const nextTab = passengersCount == nextPassengerTab ? "booking-address" : nextPassengerTab.toString();
                handleNext(nextTab);
              }}>
              Next
            </Button>
          </div>
        </Space>

        {v2 && (
          <Drawer
            placement="bottom"
            open={visaDrawerOpen}
            onClose={() => setVisaDrawerOpen(false)}
            title={visaPopoverTitle}
            className="visa-info-drawer"
            height="auto"
          >
            {visaPopoverContent}
          </Drawer>
        )}
      </div>
    );
  };

  const BookingAddress = () => {
    const filterCities = (selectedState) => {
      const storedCities = customCookies.get(`cities-${selectedState}`);
      (storedCities === undefined
        ? listApi.getCities({ state: selectedState })
        : storedArray$(storedCities)
      ).then((filteredCities) => {
        customCookies.set(`cities-${selectedState}`, JSON.stringify(filteredCities));
        setFilteredCities(filteredCities);
      });
    };

    const filterStates = (selectedCity, option) => {
      let filteredStates = states;
      let filteredCountries = countries;

      if (selectedCity) {
        filteredStates = states.filter(({ value }) => value === option.state);
        filteredCountries = countries.filter(({ value }) => value === option.country);
      } else {
        getCitiesByCountry(selectedCountry);
      }

      setFilteredStates(filteredStates);
      setFilteredCountries(filteredCountries);
    };

    const changeCountry = (selectedCountry) => {
      setSelectedCountry(selectedCountry);
      getCitiesByCountry(selectedCountry);
    };

    const getCitiesByCountry = (country) => {
      const storedCitiesByCountry = customCookies.get(`cities-${country}`);
      Promise.all([
        storedCitiesByCountry === undefined
          ? listApi.getCities({ country })
          : storedArray$(storedCitiesByCountry),
      ]).then(([cities]) => {
        if (cities.length > 0) {
          customCookies.set(`cities-${country}`, JSON.stringify(cities));
        }
        setFilteredCities(cities);
        setSelectedCountry(country);

        const states = listApi.getStatesOptionsByCities(cities);
        setFilteredStates(states);
      });
    };

    return (
      <div id="booking-address">
        <Row gutter={[20, 20]}>
          <Col span={24}>
            <Form.Item 
              label="Address 1"
              name="address1"
              dependencies={["city", "state", "postcode", "country"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const state = getFieldValue("state");
                    const city = getFieldValue("city");
                    const postcode = getFieldValue("postcode");
                    const country = getFieldValue("country");

                    if (value || state || city || postcode || country) {
                      if (!value) {
                        return Promise.reject(
                          new Error("This field cannot be empty")
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                }),
              ]}>
              <Input placeholder="Street address (e.g. No. 12, Jalan Ampang)"/>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Address 2" name="address2">
              <Input placeholder="Apartment, suite, unit, block, floor, taman, kampung, etc. (optional)"/>
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              label="State"
              name="state"
              dependencies={["address1", "city", "postcode", "country"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const address1 = getFieldValue("address1");
                    const city = getFieldValue("city");
                    const postcode = getFieldValue("postcode");
                    const country = getFieldValue("country");

                    if (address1 || city || postcode || country) {
                      if (!value) {
                        return Promise.reject(
                          new Error("This field cannot be empty")
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                }),
              ]}>
              <Select
                suffixIcon={<Icon path={mdiMenuDown} size={1} />}
                showSearch
                allowClear
                placeholder="Please Select"
                options={filteredStates}
                onChange={filterCities}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              label="City"
              name="city"
              dependencies={["address1", "state", "postcode", "country"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const address1 = getFieldValue("address1");
                    const state = getFieldValue("state");
                    const postcode = getFieldValue("postcode");
                    const country = getFieldValue("country");

                    if (address1 || state || postcode || country) {
                      if (!value) {
                        return Promise.reject(
                          new Error("This field cannot be empty")
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                }),
              ]}>
              <Select
                suffixIcon={<Icon path={mdiMenuDown} size={1} />}
                showSearch
                allowClear
                placeholder="Please Select"
                options={filteredCities}
                onChange={filterStates}
                // filterOption={false}
                // onSearch={fetchCities}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Postcode"
              name="postcode"
              dependencies={["address1", "state", "city", "country"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const address1 = getFieldValue("address1");
                    const state = getFieldValue("state");
                    const city = getFieldValue("city");
                    const country = getFieldValue("country");

                    if (address1 || state || city || country) {
                      if (!value) {
                        return Promise.reject(
                          new Error("This field cannot be empty")
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                }),
              ]}>
              <Input type="number" placeholder="Postcode (e.g. 55100)" />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Country"
              name="country"
              dependencies={["address1", "state", "city", "postcode"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const address1 = getFieldValue("address1");
                    const state = getFieldValue("state");
                    const city = getFieldValue("city");
                    const postcode = getFieldValue("postcode");

                    if (address1 || state || city || postcode) {
                      if (!value) {
                        return Promise.reject(
                          new Error("This field cannot be empty")
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                }),
              ]}>
              <Select
                suffixIcon={<Icon path={mdiMenuDown} size={1} />}
                showSearch
                allowClear
                placeholder="Please Select"
                options={filteredCountries}
                onChange={changeCountry}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div id="passenger-info" className="b2c2b-section">
      <h1>Passenger Info</h1>

      <div>
        {(selectedPax ?? 0) === 0 ? (
          <div className="passenger-info-pending">
            <p>No Traveler Selected</p>
            <p>Please <span className="passenger-info-pending__link" onClick={onSelectTraveler}>select traveler</span> to proceed</p>
          </div>
        ) : (
          <>
            <Form.List name="passengers">
              {(fields, actions) => {
                const passengerTabs = (field, index: number, passengerNumber: number) => (
                  <Tabs
                    key={field.key}
                    className="passenger-tabs"
                    activeKey={activeTab}
                    onChange={onTabChange}
                    tabPosition="left"
                    items={[{
                      key: index.toString(),
                      label: <>
                              <Tooltip title={`Passenger ${passengerNumber}`}>
                                <div className="passenger-info-header">
                                  <span className="passenger-info-header-desktop"> Passenger </span>
                                  <span className="passenger-info-header-mobile"> <Icon path={mdiAccount} size={1} /> </span>
                                  {passengerNumber}
                                </div>
                              </Tooltip>
                            </>,
                      children: PassengerForm(field),
                    }]}
                  />
                );

                if (!cabinMeta) {
                  return fields.map((field, index) => passengerTabs(field, index, index + 1));
                }

                const groups: { cabinNo: number; members: { field: any; index: number; passengerNumber: number }[] }[] = [];
                fields.forEach((field, index) => {
                  const meta = cabinMeta[index];
                  const cabinNo = meta ? meta.cabinNo : 1;
                  const passengerNumber = meta ? meta.localIndex + 1 : index + 1;
                  let group = groups.find((g) => g.cabinNo === cabinNo);
                  if (!group) {
                    group = { cabinNo, members: [] };
                    groups.push(group);
                  }
                  group.members.push({ field, index, passengerNumber });
                });

                return groups.map((group) => (
                  <div className="cruise-form-group" key={group.cabinNo}>
                    <div className="cruise-form-group_header">
                      <span className="cruise-passenger-cabin-badge">Cabin {group.cabinNo}</span>
                      <span className="cruise-form-group_rule" />
                    </div>
                    {group.members.map(({ field, index, passengerNumber }) => passengerTabs(field, index, passengerNumber))}
                  </div>
                ));
              }}
            </Form.List>

            {(() => {
              const addressTabs = (
                <Tabs
                  activeKey={activeTab}
                  onChange={onTabChange}
                  tabPosition="left"
                  items={[
                    {
                      key: "booking-address",
                      forceRender: true,
                      label: <>
                              <Tooltip title="Address">
                                <div className="passenger-info-header">
                                  <span className="passenger-info-header-desktop"> Address </span>
                                  <span className="passenger-info-header-mobile"> <Icon path={mdiReceiptTextOutline} size={1} /> </span>
                                </div>
                              </Tooltip>
                            </>,
                      children: BookingAddress(),
                    },
                  ]}
                />
              );

              if (!cabinMeta) return addressTabs;

              return <div className="cruise-form-group">{addressTabs}</div>;
            })()}
          </>
        )}
      </div>
    </div>
  )
};

export default PassengerInfo;
